import React, { useEffect, useState } from 'react';
import { PlusIcon, Trash2Icon } from 'lucide-react';
import { CITIES, KINDS, Kind, Place } from '../../data/smaken';
import { ImageInput } from './ImageInput';
import { Field, FieldLabel, PrimaryButton, SelectField, TextArea } from './ui';
interface Props {
  place: Place;
  onSave: (place: Place) => void;
}

/** Text areas edit lists as one entry per line (or paragraph). */
const lines = (value: string) => value.split('\n').map(l => l.trim()).filter(Boolean);
const paragraphs = (value: string) => value.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
export function PartnerProfileEditor({
  place,
  onSave
}: Props) {
  const [draft, setDraft] = useState<Place>(place);
  const [description, setDescription] = useState(place.description.join('\n\n'));
  const [weLike, setWeLike] = useState(place.weLike.join('\n'));
  const [tags, setTags] = useState(place.tags.join(', '));
  const [saved, setSaved] = useState(false);

  // Reset when switching partner.
  useEffect(() => {
    setDraft(place);
    setDescription(place.description.join('\n\n'));
    setWeLike(place.weLike.join('\n'));
    setTags(place.tags.join(', '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place.slug]);
  useEffect(() => {
    if (!saved) return;
    const t = window.setTimeout(() => setSaved(false), 1800);
    return () => window.clearTimeout(t);
  }, [saved]);
  const set = <K extends keyof Place,>(key: K, value: Place[K]) => setDraft(d => ({
    ...d,
    [key]: value
  }));
  const save = () => {
    onSave({
      ...draft,
      name: draft.name.trim() || 'Namnlös verksamhet',
      description: paragraphs(description),
      weLike: lines(weLike),
      tags: tags.split(',').map(t => t.trim()).filter(Boolean),
      gallery: draft.gallery.filter(Boolean)
    });
    setSaved(true);
  };
  const setGallery = (index: number, value: string) => set('gallery', draft.gallery.map((g, i) => i === index ? value : g));
  return <div className="space-y-10" data-id="element-177" data-mp="2fb88moi6rmat">
      <section className="grid gap-6 lg:grid-cols-2" data-id="element-178" data-mp="2fagn66iaz4y9">
        <div className="space-y-5" data-id="element-179" data-mp="2acycaakq6fnm">
          <Field label="Namn" value={draft.name} onChange={v => set('name', v)} data-id="element-180" data-mp="2abn6k57pyxtv" />
          <Field label="Adress på sajten" value={draft.slug} onChange={v => set('slug', v)} hint={`smaken.se/plats/${draft.slug}`} data-id="element-181" data-mp="2p1ujk5eyjon6" />
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-182" data-mp="1wcdw7m672xxt">
            <SelectField label="Stad" value={String(draft.city)} onChange={v => set('city', v)} options={Object.keys(CITIES).map(c => ({
            value: c,
            label: c
          }))} data-id="element-183" data-mp="1wf01ff5m51cv" />
            <SelectField label="Typ av ställe" value={draft.kind} onChange={v => set('kind', v as Kind)} options={KINDS.map(k => ({
            value: k.id,
            label: `${k.label} — ${k.sub}`
          }))} data-id="element-184" data-mp="21bgzp26j0w3o" />
          </div>
          <Field label="Taggar" value={tags} onChange={setTags} placeholder="Mackverk, Deli, Hantverksbak" hint="Separera med komma." data-id="element-185" data-mp="3cti1vyy5lrdu" />
        </div>
        <ImageInput label="Omslagsbild" value={draft.image} onChange={v => set('image', v)} aspect="aspect-[3/2]" data-id="element-186" data-mp="3pastyc06bndu" />
      </section>

      <section className="space-y-5 border-t border-gray-100 pt-8" data-id="element-187" data-mp="3aebizlvwcart">
        <h3 className="text-sm font-semibold text-gray-900" data-id="element-188" data-mp="3fcmstwm6yla1">Berättelsen</h3>
        <Field label="Ingress" value={draft.blurb} onChange={v => set('blurb', v)} placeholder="En självsäker mening." data-id="element-189" data-mp="6v8ixvha7wwp" />
        <TextArea label="Om" rows={6} value={description} onChange={setDescription} hint="Tom rad mellan styckena." data-id="element-190" data-mp="bttjjw0g4ilo" />
        <TextArea label="Det här gillar vi" rows={4} value={weLike} onChange={setWeLike} hint="En sak per rad." data-id="element-191" data-mp="3tathqn95xaxk" />
      </section>

      <section className="border-t border-gray-100 pt-8" data-id="element-192" data-mp="22p5qzmib3sf">
        <FieldLabel data-id="element-193" data-mp="ptullesg5w2q">Galleri</FieldLabel>
        <div className="grid gap-4 sm:grid-cols-3" data-id="element-194" data-mp="pstaxbfcfblj">
          {draft.gallery.map((src, i) => <div key={i} className="relative" data-id="element-195" data-mp="un7a7ox4m2vh" data-mpi={i}>
              <ImageInput label={`Bild ${i + 1}`} value={src} onChange={v => setGallery(i, v)} aspect="aspect-[3/2]" data-id="element-196" data-mp="333gkqz0xmper" />
            </div>)}
          {draft.gallery.length < 6 && <button type="button" onClick={() => set('gallery', [...draft.gallery, ''])} className="mt-6 flex aspect-[3/2] items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-gray-200 text-sm text-gray-500 transition-colors hover:border-gray-400" data-id="element-197" data-mp="2t5amcrec41en">
              <PlusIcon className="h-4 w-4" data-id="element-198" data-mp="3gubx4lan3on3" /> Lägg till bild
            </button>}
        </div>
      </section>

      <section className="grid gap-8 border-t border-gray-100 pt-8 lg:grid-cols-2" data-id="element-199" data-mp="3c0qyqs54wsnd">
        <div className="space-y-5" data-id="element-200" data-mp="373qtukk0tvxu">
          <h3 className="text-sm font-semibold text-gray-900" data-id="element-201" data-mp="8dqo54i0cvjz">Kontakt</h3>
          <Field label="Adress" value={draft.address} onChange={v => set('address', v)} data-id="element-202" data-mp="8fv089q290vc" />
          <Field label="E-post" type="email" value={draft.email ?? ''} onChange={v => set('email', v || undefined)} data-id="element-203" data-mp="3l78m1zrfqcj" />
          <Field label="Instagram" value={draft.instagram ?? ''} onChange={v => set('instagram', v || undefined)} placeholder="@lillaverket" data-id="element-204" data-mp="fxjgmhrwhoon" />
        </div>
        <div data-id="element-205" data-mp="kv1fffc7enix">
          <h3 className="mb-5 text-sm font-semibold text-gray-900" data-id="element-206" data-mp="kvb8jbsjpasu">Öppettider</h3>
          <div className="space-y-2" data-id="element-207" data-mp="pxabikkdi5p9">
            {draft.hours.map((row, i) => <div key={i} className="flex gap-2" data-id="element-208" data-mp="utr0ti3b6q3x" data-mpi={i}>
                <input value={row.day} onChange={e => set('hours', draft.hours.map((h, j) => j === i ? {
              ...h,
              day: e.target.value
            } : h))} placeholder="Mån–Fre" aria-label="Dag" className="w-32 rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-black" data-id="element-209" data-mp="ywdulojjil3r" />
                <input value={row.time} onChange={e => set('hours', draft.hours.map((h, j) => j === i ? {
              ...h,
              time: e.target.value
            } : h))} placeholder="07–17" aria-label="Tid" className="min-w-0 flex-1 rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-black" data-id="element-210" data-mp="1dj72i8qqcolx" />
                <button type="button" aria-label="Ta bort rad" onClick={() => set('hours', draft.hours.filter((_, j) => j !== i))} className="flex w-10 items-center justify-center rounded-xl text-gray-400 hover:bg-gray-100 hover:text-gray-900" data-id="element-211" data-mp="12o55bku0bpzx">
                  <Trash2Icon className="h-4 w-4" data-id="element-212" data-mp="1llg28ys627c7" />
                </button>
              </div>)}
            <button type="button" onClick={() => set('hours', [...draft.hours, {
            day: '',
            time: ''
          }])} className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-900" data-id="element-213" data-mp="1gljxgxbnabvl">
              <PlusIcon className="h-3.5 w-3.5" data-id="element-214" data-mp="3if9hlrrzkg10" /> Lägg till rad
            </button>
          </div>
        </div>
      </section>

      <div className="sticky bottom-4 flex items-center justify-end gap-4 rounded-full bg-white/95 p-2 pl-6 shadow-floating" data-id="element-215" data-mp="22tz5acb5zwrh">
        <span className="text-sm text-gray-500" aria-live="polite" data-id="element-216" data-mp="1o4b2f6nc4q1n">{saved ? 'Sparat — syns direkt på sajten.' : 'Ändringar sparas när du klickar.'}</span>
        <PrimaryButton onClick={save} data-id="element-217" data-mp="1t22tjyg5o84s">Spara profil</PrimaryButton>
      </div>
    </div>;
}